import {
  BellIcon,
  CalendarIcon,
  ChatAlt2Icon,
  CheckCircleIcon,
  CollectionIcon,
  FlagIcon,
  GiftIcon,
  GlobeAltIcon,
  HandIcon,
  IdentificationIcon,
  InboxIcon,
  PhotographIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import MessengerChat from '../mocks/MessengerChat';
import MessengerSetup from '../mocks/MessengerSetup';
import MessengerTakeover from '../mocks/MessengerTakeover';
import type { ChannelContent, DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';

const BRAND: DemoBrand = {
  name: 'Studio Lumière',
  initial: 'L',
  bg: 'bg-[#be185d]',
};

/*
 * Facts this page relies on (checked 2026-10-21, docs/agents/messenger.mdx
 * and docs/notes/agents-channels.md): Messenger is in FREE_PLAN_CHANNELS;
 * connecting is a Facebook sign-in with no app review for the owner; one
 * agent per Page (connecting elsewhere pauses it); a human reply from the
 * Page inbox sets `human_until` = last reply + 1 h for that conversation;
 * photos are read with image attachments on and a vision model; videos and
 * files get "please type"; RECONNECT NEEDED + one email per dead token;
 * Instagram DMs are not a channel. Voice notes are deliberately not
 * mentioned: transcription shipped but isn't live-tested or documented.
 */
export function useMessengerContent(): ChannelContent {
  const { t } = useChannelsT();
  const { t: tc } = useLocale('chatbots');

  return {
    key: 'messenger',
    name: 'Messenger',
    logo: 'FaFacebookMessenger',
    seoTitle: t('Facebook Messenger Chatbot: AI Agent for Your Facebook Page'),
    seoDescription: t(
      'An AI chatbot for your Facebook Page. It answers Messenger 24/7 from your own knowledge, books appointments and lets you step in at any time. Free to start.',
    ),
    hero: {
      label: t('Facebook Messenger AI chatbot'),
      title: t('Your Facebook Page, answered in seconds.'),
      accent: t('Even on your day off.'),
      lead: t(
        'Connect your Page with one Facebook sign-in. Your AI agent answers Messenger from your website and documents, books appointments, and steps back whenever you reply yourself.',
      ),
      facts: [
        t('Works on the Free plan'),
        t('Sign in with Facebook, no developer app'),
        t('Step in at any time'),
      ],
      description: t(
        'Example: a customer asks a hair salon’s Facebook Page for a haircut on Friday. The agent checks the calendar, offers two free times and books the one the customer picks.',
      ),
      demo: (
        <MessengerChat
          brand={BRAND}
          lines={[
            {
              from: 'customer',
              text: t('Hi! Do you have anything free for a haircut on Friday?'),
              at: 600,
            },
            {
              from: 'agent',
              text: t(
                'Hi! Friday at 10:30 or 15:00 is free. Which one suits you?',
              ),
              typingAt: 1100,
              at: 2100,
            },
            {
              from: 'customer',
              text: t('15:00, please. I’m Lena.'),
              at: 3200,
            },
            {
              from: 'agent',
              text: t(
                'You’re booked, Lena: haircut on Friday at 15:00. See you then! ✂️',
              ),
              typingAt: 3600,
              at: 4500,
            },
          ]}
        />
      ),
      steps: [
        {
          icon: CalendarIcon,
          title: t('Checked free times'),
          via: tc('Google Calendar'),
          at: 1500,
        },
        {
          icon: CheckCircleIcon,
          title: t('Booked the haircut'),
          via: tc('Google Calendar'),
          at: 3900,
        },
      ],
    },
    jobs: {
      title: t('Your Page inbox, handled.'),
      tail: t('Questions, bookings and leads.'),
      lead: t(
        'Messages to your Page often arrive when nobody’s there to answer. Your agent replies in seconds, from what you’ve taught it, and does the follow-up.',
      ),
      items: [
        {
          icon: ChatAlt2Icon,
          title: t('Answers questions'),
          detail: t(
            'Opening hours, prices, services and policies, straight from your website and documents.',
          ),
        },
        {
          icon: CalendarIcon,
          title: t('Books appointments'),
          detail: t(
            'Checks when you’re free and books the time in your Google Calendar.',
          ),
        },
        {
          icon: IdentificationIcon,
          title: t('Captures leads'),
          detail: t(
            'Collects names, emails and anything else you ask for, one question at a time, and saves them to Leads.',
          ),
        },
        {
          icon: HandIcon,
          title: t('Greets new people'),
          detail: t(
            'When someone taps Get Started, it says hello and what it can help with.',
          ),
        },
        {
          icon: PhotographIcon,
          title: t('Reads photos'),
          detail: t(
            'Turn on image attachments and it reads the photos people send, with a model that can see images.',
          ),
        },
        {
          icon: GlobeAltIcon,
          title: t('Speaks their language'),
          detail: t(
            'Replies in the language each customer writes in, from the same knowledge.',
          ),
        },
      ],
    },
    setup: {
      title: t('Connect your Page in a minute.'),
      tail: t('No developer account, no app review.'),
      steps: [
        {
          title: t('Build your agent'),
          detail: t(
            'Add your website, menu or documents and write instructions in plain words. Try it in the Playground first.',
          ),
        },
        {
          title: t('Choose your Pages'),
          detail: t(
            'Click Connect on Facebook Messenger, sign in with the Facebook account that manages your Page and pick the Pages your agent answers for.',
          ),
        },
        {
          title: t('Send a test message'),
          detail: t(
            'Message your Page from another account, or open its m.me link, and ask a question.',
          ),
        },
      ],
      guide: 'https://easy-peasy.ai/docs/agents/messenger',
      guideLabel: t('Read the Messenger setup guide'),
      description: t(
        'Facebook’s window for choosing Pages, with the salon’s Page selected, and the agent’s list of connected Pages showing it as active.',
      ),
      visual: <MessengerSetup brand={BRAND} otherPage='Lumière Academy' />,
    },
    spotlight: {
      title: t('Step in whenever you want.'),
      tail: t('Your agent steps back.'),
      lead: t(
        'Reply to a customer from your Page inbox and the agent stays quiet in that conversation until an hour after your last reply. Other conversations carry on as usual.',
      ),
      points: [
        t('Reply from Meta Business Suite, as you always have'),
        t('Instant replies and away messages don’t pause the agent'),
        t('Pause a whole Page with one switch'),
        t('The agent picks up again after an hour'),
      ],
      description: t(
        'A conversation in the Page inbox where the agent answered first and the owner then replied, with a note that the agent steps back in that conversation.',
      ),
      visual: (
        <MessengerTakeover
          customer='Lena Fischer'
          messages={[
            {
              from: 'customer',
              text: t('Can I bring my daughter for a trim too?'),
            },
            {
              from: 'agent',
              text: t(
                'Of course! A kids’ trim takes 20 minutes. Shall I add it after yours?',
              ),
            },
            {
              from: 'owner',
              text: t(
                'Hi Lena, it’s Nadia! I’ll do both cuts myself. See you Friday 😊',
              ),
            },
          ]}
        />
      ),
    },
    rules: {
      title: t('Good to know.'),
      tail: t('Messenger, in plain words.'),
      items: [
        {
          icon: GiftIcon,
          title: t('Free plan included'),
          detail: t(
            'Messenger works on every plan, including Free. Actions, such as bookings, need a paid plan.',
          ),
        },
        {
          icon: FlagIcon,
          title: t('Facebook Pages only'),
          detail: t(
            'You need a Facebook Page and admin access to it. Personal profiles can’t be connected.',
          ),
        },
        {
          icon: PhotographIcon,
          title: t('Photos and files'),
          detail: t(
            'It reads photos when image attachments are on and your model can see images. For videos and files, it asks people to type their question.',
          ),
        },
        {
          icon: CollectionIcon,
          title: t('One agent per Page'),
          detail: t(
            'Each Page answers through one agent. Connect it to another agent and it pauses on the first.',
          ),
        },
        {
          icon: BellIcon,
          title: t('Reconnect alerts'),
          detail: t(
            'If Facebook disconnects a Page, for example after a password change, it shows Reconnect needed and we email you.',
          ),
        },
        {
          icon: InboxIcon,
          title: t('Every chat in one inbox'),
          detail: t(
            'Messenger chats show up in Conversations, next to your website, WhatsApp, Telegram and LINE chats.',
          ),
        },
      ],
    },
    faqs: [
      {
        question: t('Is the Messenger chatbot free?'),
        answer: t(
          'Yes. Messenger works on every plan, including Free. Actions such as bookings and collecting details are on paid plans.',
        ),
      },
      {
        question: t('Do I need a developer account or Meta’s app review?'),
        answer: t(
          'No. You sign in with Facebook, approve the permissions and pick your Pages. Everything else is set up for you.',
        ),
      },
      {
        question: t('Can I still reply to customers myself?'),
        answer: t(
          'Yes. Reply from your Page inbox in Meta Business Suite and the agent steps back in that conversation until an hour has passed since your last reply. Instant replies and away messages don’t count.',
        ),
      },
      {
        question: t('Can one agent answer for several Pages?'),
        answer: t(
          'Yes. Pick every Page it should answer for when you connect. Each Page answers through one agent at a time, so connecting a Page to another agent pauses it on the first.',
        ),
      },
      {
        question: t('Does it work with Instagram?'),
        answer: t(
          'No. It answers Messenger messages sent to your Facebook Page. Instagram direct messages aren’t a channel.',
        ),
      },
      {
        question: t('Do I need to say it’s an AI?'),
        answer: t(
          'Meta requires it in some places, such as California and Germany. Add a line like “Introduce yourself as our AI assistant” to your agent’s instructions.',
        ),
      },
      {
        question: t('What if Facebook disconnects my Page?'),
        answer: t(
          'That happens after a password change, or when the app is removed from your business integrations. The Page shows Reconnect needed and we email you. Click Reconnect and sign in again.',
        ),
      },
      {
        question: t('Which languages does it speak?'),
        answer: t(
          'It replies in the language each customer writes in, more than 80 of them, from the same knowledge.',
        ),
      },
    ],
    finalTitle: t('Your Facebook Page could be answering by lunchtime.'),
    finalExchange: {
      question: t('Do you do balayage?'),
      answer: t(
        'Yes! It takes about 3 hours. Want to see Saturday’s free times?',
      ),
    },
  };
}
